import { useState, useEffect } from 'react';
import { ArrowLeft, Calendar, MapPin, Clock, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CroppedImage } from '@/components/ui/cropped-image';
import { Event, Race } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { WinnersGallery } from './WinnersGallery';
import { coverUrl } from '@/utils/coverImage';
import { EventPassTicket, useEventPassRiders } from './EventPassTicket';
import { EVENT_PASS_MIN_RACES } from '@/utils/cart';

interface EventDetailProps {
  event: Event;
  onBack: () => void;
  onRaceSelect: (race: Race) => void;
}

export const EventDetail = ({ event, onBack, onRaceSelect }: EventDetailProps) => {
  const { t } = useTranslation();
  const [races, setRaces] = useState<Race[]>([]);
  const [loading, setLoading] = useState(true);
  const { data: passRiders = [] } = useEventPassRiders(event.id);
  const passRaceIds = new Set(
    passRiders.filter(r => r.classes.length >= EVENT_PASS_MIN_RACES).flatMap(r => r.classes.map(c => c.swissRaceId))
  );

  useEffect(() => {
    fetchRaces();
  }, [event.id]);

  const fetchRaces = async () => {
    try {
      const { data, error } = await supabase
        .from('races')
        .select('id, event_id, swiss_equestrian_race_id, name, category, race_date, race_time, status, race_order, race_type, batch_job_id, participant_list_uploaded, created_at, updated_at')
        .eq('event_id', event.id)
        .order('race_order', { ascending: true })
        .order('race_date', { ascending: true })
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching races:', error);
        return;
      }

      const sortedRaces = (data || []).sort((a, b) => {
        if (a.race_order !== null && b.race_order !== null) {
          return a.race_order - b.race_order;
        }
        if (a.race_order !== null && b.race_order === null) return -1;
        if (a.race_order === null && b.race_order !== null) return 1;
        const aNumber = parseInt(a.name.replace(/\D/g, '')) || 0;
        const bNumber = parseInt(b.name.replace(/\D/g, '')) || 0;
        return aNumber - bNumber;
      });

      const typedRaces = sortedRaces.map(race => ({
        ...race,
        race_type: (race.race_type as 'STANDARD' | 'GROUPED') || 'STANDARD',
      }));
      setRaces(typedRaces);
    } catch (error) {
      console.error('Error fetching races:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'B100': return 'bg-red-100 text-red-800 border-red-200';
      case 'B/R 90': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'L80': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-surface-container-high text-on-surface';
    }
  };

  if (loading) {
    return (
      <div>
        {/* Hero skeleton */}
        <Skeleton className="w-full h-72 md:h-96 rounded-none" />
        <div className="container mx-auto px-4 py-8 space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-5 w-96" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-56 rounded-md" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Full-bleed Hero Banner */}
      <div className="relative w-full h-72 md:h-96 overflow-hidden">
        <CroppedImage
          src={coverUrl(event.cover_image_url, 'hero') || 'https://images.unsplash.com/photo-1553978297-833d24c4736f?w=1200&h=400&fit=crop'}
          alt={event.name}
          className="w-full h-full object-cover"
          cropTop={event.cover_crop_top}
          fallbackSrc="https://images.unsplash.com/photo-1553978297-833d24c4736f?w=1200&h=400&fit=crop"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />

        {/* Back button overlay */}
        <Button
          onClick={onBack}
          variant="ghost"
          className="absolute top-6 left-6 z-10 text-white/90 hover:text-white hover:bg-white/10 backdrop-blur-sm"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('eventDetail.backToEvents')}
        </Button>

        {/* Event info overlay */}
        <div className="absolute bottom-8 left-0 right-0 px-6 md:px-12">
          <p className="uppercase tracking-[0.2em] text-white/60 text-xs font-medium mb-3">
            {event.location}
          </p>
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-3 leading-[1.1]">
            {event.name}
          </h1>
          <div className="flex flex-wrap gap-4 text-sm text-white/85">
            <div className="flex items-center">
              <MapPin className="w-4 h-4 mr-1.5 opacity-70" />
              {event.location}
            </div>
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-1.5 opacity-70" />
              {format(new Date(event.start_date), 'MMM d')} – {format(new Date(event.end_date), 'd, yyyy')}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-10">
        {/* Winners Gallery */}
        <EventPassTicket
          eventUuid={event.id}
          eventSwissId={event.swiss_equestrian_id || ''}
          eventName={event.name}
          onOpenRace={(rid) => { const r = races.find(x => x.swiss_equestrian_race_id === rid); if (r) onRaceSelect(r); }}
        />

        {event.has_winners && <WinnersGallery event={event} />}

        {/* Race Categories Section */}
        <div className="mb-6">
          <h2 className="font-serif italic text-2xl mb-1">
            {t('eventDetail.raceCategories')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('eventDetail.raceSubtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {races.map((race) => (
            <div
              key={race.id}
              className="group bg-surface-container-lowest rounded-md p-5 cursor-pointer transition-all duration-300 hover:shadow-hover hover:scale-[1.02] active:scale-[0.98]"
              onClick={() => onRaceSelect(race)}
            >
              <div className="mb-3 flex flex-wrap items-center gap-1.5">
                <Badge className={`${getCategoryColor(race.category)} text-xs`}>
                  {race.category}
                </Badge>
                {passRaceIds.has(race.swiss_equestrian_race_id) && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-surface-container-low px-2 py-0.5 text-xs font-medium text-on-surface">
                    <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-primary" />
                    {t('eventPass.available')}
                  </span>
                )}
              </div>

              <h3 className="font-semibold text-base mb-1 group-hover:text-primary transition-colors">
                {t('races.raceNumber', { number: race.race_order || races.indexOf(race) + 1 })}
              </h3>
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                {race.name}
              </p>

              <div className="space-y-1.5 mb-4">
                <div className="flex items-center text-xs text-muted-foreground">
                  <Calendar className="w-3.5 h-3.5 mr-1.5 text-primary/60" />
                  {format(new Date(race.race_date), 'MMM d, yyyy')}
                </div>
                {race.race_time && (
                  <div className="flex items-center text-xs text-muted-foreground">
                    <Clock className="w-3.5 h-3.5 mr-1.5 text-primary/60" />
                    {race.race_time}
                  </div>
                )}
              </div>

              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs uppercase tracking-wider"
                onClick={(e) => {
                  e.stopPropagation();
                  onRaceSelect(race);
                }}
              >
                <Eye className="w-3.5 h-3.5 mr-1.5" />
                {t('races.participants')}
              </Button>
            </div>
          ))}
        </div>

        {races.length === 0 && !loading && (
          <div className="text-center py-16">
            <h3 className="text-xl font-semibold mb-2 font-serif">{t('races.noRaces')}</h3>
            <p className="text-muted-foreground">
              {t('races.noRacesDescription')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
